import type { ReactElement, ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { A3Sheet } from "../../icons/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { MySolvesView } from "./types.js";

/** Data-blind Personal Home list. The host owns both navigation actions. */
export function MySolvesWidget({
  data,
}: WidgetBodyProps<MySolvesView>): ReactElement {
  const actions = useWidgetActions();

  return (
    <div className="flex flex-col gap-3">
      {data.solves.length === 0 ? (
        <div className="flex min-h-22 flex-col items-start justify-center gap-1 py-2">
          <p className="text-sm font-medium text-pb-text">No open SOLVEs</p>
          <p className="text-xs text-pb-text-secondary">
            Draft, active, and verified SOLVEs you collaborate on will appear
            here.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.solves.slice(0, 5).map((solve) => {
            const label: ReactNode = (
              <>
                <A3Sheet
                  className="mt-0.5 h-4 w-4 shrink-0 text-pb-cyan-text"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm leading-snug text-pb-text">
                    {solve.title}
                  </span>
                  <span className="block truncate text-xs leading-snug text-pb-text-muted">
                    {solve.status} · {solve.currentPhase}
                  </span>
                </span>
              </>
            );
            return (
              <li key={solve.id}>
                {actions?.openSolve ? (
                  // eslint-disable-next-line ds/no-hand-built -- kept: a clickable row with its own inset and a pulled-out hover; ListItem has no inline padding and its own row layout, which AP-07's selection fill does not cover.
                  <button
                    type="button"
                    onClick={() => actions.openSolve?.(solve.id, solve.title)}
                    className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-2.5 rounded-md px-2 py-2 text-start transition-colors hover:pb-overlay-paint focus-ring"
                  >
                    {label}
                  </button>
                ) : (
                  <div className="flex items-start gap-2.5 py-1.5">{label}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {actions?.openSolveHome ? (
        // eslint-disable-next-line ds/no-hand-built -- kept: a 14px secondary-ink text action with a chevron; Button link is underlined at inline size, and no approved look row lists the change.
        <button
          type="button"
          onClick={actions.openSolveHome}
          className="inline-flex items-center self-start text-sm font-medium text-pb-text-secondary transition-colors hover:text-pb-text focus-ring"
        >
          Open SOLVE home
          <ChevronRight className="ms-1 h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
